<script setup lang="ts">
type CornerIcon = "fish" | "droplets" | "footprints" | "waves";

interface CornerItem {
  title: string;
  caption: string;
  image: string;
  image_alt?: string;
  icon?: CornerIcon;
}

interface CornersGallery {
  eyebrow?: string;
  header: string;
  intro?: string;
  items: CornerItem[];
}

defineProps<{
  block: CornersGallery;
}>();
</script>

<template>
  <section
    class="relative overflow-hidden bg-gradient-to-b from-white via-amber-50/30 to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900"
  >
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -top-32 left-1/2 h-80 w-80 -translate-x-1/2 rounded-full bg-amber-200/25 blur-3xl dark:bg-amber-500/10"
    />
    <div
      aria-hidden="true"
      class="pointer-events-none absolute -bottom-32 -right-16 h-80 w-80 rounded-full bg-amber-400/15 blur-3xl dark:bg-amber-400/10"
    />

    <div class="relative mx-auto w-full max-w-7xl px-4 py-8 sm:px-6 md:px-8 md:py-12">
      <header data-reveal class="mb-8 md:mb-10 max-w-3xl">
        <span
          v-if="block.eyebrow"
          class="font-body text-xs uppercase tracking-[0.3em] text-amber-600 dark:text-amber-400"
        >
          {{ block.eyebrow }}
        </span>
        <h2
          class="mt-3 font-display text-3xl italic leading-tight text-slate-900 sm:text-4xl md:text-5xl lg:text-6xl dark:text-white"
        >
          {{ block.header }}
        </h2>
        <div class="mt-3 h-1 w-16 rounded-full bg-amber-400" />
        <p
          v-if="block.intro"
          class="mt-4 font-body text-base leading-relaxed text-slate-600 sm:text-lg lg:text-xl dark:text-slate-300"
        >
          {{ block.intro }}
        </p>
      </header>

      <div
        class="grid grid-cols-1 gap-4 sm:grid-cols-2 md:gap-5"
        :class="[
          block.items.length === 3 ? 'lg:grid-cols-3' : '',
          block.items.length >= 4 ? 'lg:grid-cols-4' : '',
        ]"
      >
        <article
          v-for="(item, i) in block.items"
          :key="i"
          :data-reveal="''"
          :data-reveal-delay="String(i % 4)"
          class="group relative flex min-h-[360px] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl sm:min-h-[400px] dark:border-slate-700 dark:bg-slate-800/60"
        >
          <div class="relative aspect-[4/3] overflow-hidden">
            <NuxtImg
              :src="item.image"
              :alt="item.image_alt ?? item.title"
              width="800"
              height="600"
              format="webp"
              loading="lazy"
              sizes="100vw sm:50vw lg:25vw"
              class="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
            <!-- Soft gradient + tonal icon badge (top-left) — amenities-style accent -->
            <div
              aria-hidden="true"
              class="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-slate-900/40 to-transparent"
            />
            <span
              v-if="item.icon"
              class="absolute left-4 top-4 inline-flex size-10 items-center justify-center rounded-full bg-white/85 text-amber-700 shadow-sm backdrop-blur-sm ring-1 ring-white/60 dark:bg-slate-900/70 dark:text-amber-300 dark:ring-white/10"
            >
              <Icon :name="`lucide:${item.icon}`" class="size-5" aria-hidden="true" />
            </span>
          </div>
          <div class="flex flex-1 flex-col p-5 sm:p-6 md:p-7">
            <h3
              class="font-display text-lg italic text-slate-900 sm:text-xl lg:text-2xl dark:text-white break-words"
            >
              {{ item.title }}
            </h3>
            <p
              class="mt-2 font-body text-sm leading-relaxed text-slate-600 sm:text-base dark:text-slate-400"
            >
              {{ item.caption }}
            </p>
          </div>
        </article>
      </div>
    </div>
  </section>
</template>
